import $, { Cash, Element } from "cash-dom";
import {cInfo, publish} from '../humans/helper';

/**
 * Register Module
 * Handles section navigation, scrolling, and intersection observers only
 * Filter UI management moved to PseudoFilter.ts for cleaner separation
 * @package KAA
 */
export default class Register {
    /**
     * Singleton instance of the component
     */
    static #instance;

    /**
     * Cash body reference
     */
    protected $body;

    /**
     * UI references
     */
    protected ui = {
        registerLink: ".js-register-link",
        registerList: ".js-register-list",
        registerSection: ".js-register-section",
    };

    /**
     * Register constructor
     */
    protected constructor() {
        this.$body = $("body");
        this.registerEvents();
        this.initIntersectionObserver();
    }

    /**
     * Returns the singleton instance of the component
     */
    public static getInstance(): Register {
        if (!Register.#instance) {
            Register.#instance = new Register();
        }
        return Register.#instance;
    }

    /**
     * Register required DOM and PubSub events
     */
    protected registerEvents(): void {
        // Prevent default click and scroll to section
        this.$body.on("click", this.ui.registerLink, (event) => this.scrollToSection(event));

        // Update section heights on viewport resize or orientation change
        window.addEventListener('resize', () => this.setupSections(this.ui.registerSection));
        window.addEventListener('orientationchange', () => this.setupSections(this.ui.registerSection));
    }

    /**
     * Setup sections
     */
    protected setupSections(section: string): void {
        const $sections = $(section);

        // Add height for each section to define sticky pos
        $sections.each((index, el) => {
            const $el = $(el);
            const height = $el.outerHeight(true);
            $el.css('--section-h', `${height}px`);
        });
    }

    /**
     * Scroll to section from link click
     */
    protected scrollToSection(event: Event): void {
        event.preventDefault();

        const $link = $(event.currentTarget as HTMLElement);
        const targetId = $link.attr("href")?.replace("#", "") ?? '';

        this.scrollToSectionById(targetId);
    }

    /**
     * Public method to scroll to a section by its ID
     */
    public scrollToSectionById(sectionId: string): void {
        const $targetSection = $(`#${sectionId}`);

        // Return if no section available
        if (!$targetSection.length) return;

        // Publish anchor scrolling
        publish('anchorScroll.start', true);

        // Calculate accurate offset
        let offsetTop = $targetSection.offset()?.top ?? 0;

        // Create a temporary element to resolve the correct top scroll
        const tempElement = document.createElement("div");
        tempElement.classList.add('register-top-helper');
        $targetSection[0]?.appendChild(tempElement);

        // Get computed height in pixels
        const registerListTopPx = parseFloat(getComputedStyle(tempElement).height);

        // Remove the temporary element after calculation
        tempElement.remove();

        // Add the custom property value to offsetTop
        offsetTop -= registerListTopPx;

        // Add view transition for desktop if possible
        // NOTE: Our view transition is quite heavy – so skipping it for mobile
        if (document.startViewTransition && !this.isMobile()) {
            // Start View Transition
            document.startViewTransition(() => {
                // Scroll to section instantly
                // and let view transitions handle the transition
                window.scrollTo({
                    top: offsetTop,
                    behavior: "instant"
                });
            });

            // Remove anchor scrolling status
            window.setTimeout(() => {
                publish('anchorScroll.end', true);
            }, 600);

            cInfo('view-transitions started');
        } else {
            // Fallback for browsers without View Transitions API
            // NOTE: scrollTo with behavior: "instant" looks hacky
            this.smoothScrollTo(offsetTop, 350); // Smooth scroll over 350ms
            cInfo('custom scroll started');
        }

        // Focus on first .js-teaser for accessibility
        const $firstTeaser = $targetSection.find('a').first();
        if ($firstTeaser.length) {
            // Focus element
            // NOTE: This will add a :focus-visible style on Safari
            // but there is no good way around
            $firstTeaser.focus({ preventScroll: true });
        }
    }

    /**
     * Smooth scrolling fallback using requestAnimationFrame
     */
    protected smoothScrollTo(targetPosition: number, duration: number): void {
        const start = window.scrollY || window.pageYOffset;
        const distance = targetPosition - start;
        let startTime: number | null = null;

        const easeOutQuad = (t: number) => 1 - (1 - t) * (1 - t);

        const animateScroll = (currentTime: number) => {
            if (!startTime) startTime = currentTime;
            const timeElapsed = currentTime - startTime;
            const progress = Math.min(timeElapsed / duration, 1);
            const easing = easeOutQuad(progress);

            window.scrollTo(0, start + distance * easing);

            if (progress < 1) {
                requestAnimationFrame(animateScroll);
            } else {
                // Remove anchor scrolling status
                window.setTimeout(() => {
                    publish('anchorScroll.end', true);
                }, 100);
            }
        };

        requestAnimationFrame(animateScroll);
    }

    /**
     * Initialize Intersection Observer for .js-register-list
     */
    protected initIntersectionObserver(): void {
        const getRootMargin = (type: string): string => {
            if (this.isMobile()) {
                return type === "list" ? "0px 0px 0px 0px" : "-60% 0px -30% 0px";
            } else {
                return type === "list" ? "0px 0px 0px 0px" : "0px 0px -30% 0px";
            }
        };

        const createObserver = (className: string, type: string): IntersectionObserver => {
            return new IntersectionObserver((entries) => {
                entries.forEach(entry => {
                    const parent = entry.target.parentElement;
                    if (entry.isIntersecting) {
                        $(parent)?.addClass(className);
                    } else {
                        $(parent)?.removeClass(className);
                    }
                });
            }, {
                root: null, // Use viewport as root
                threshold: 0, // Trigger when first px of the element is visible
                rootMargin: getRootMargin(type)
            });
        };

        // Observer 1 for .js-register-list with list rootMargin
        const listObserver = createObserver("is-in-view", "list");

        // Observer 2 for .js-register-list with filter rootMargin
        const filterObserver = createObserver("has-filter-in-view", "filter");

        $(this.ui.registerList).each((_, el) => {
            listObserver.observe(el);
            filterObserver.observe(el);
        });

        // Update rootMargin on resize for both observers
        window.addEventListener("resize", () => {
            listObserver.disconnect();
            filterObserver.disconnect();

            const updatedListObserver = createObserver("is-in-view", "list");
            const updatedFilterObserver = createObserver("has-filter-in-view", "filter");

            $(this.ui.registerList).each((_, el) => {
                updatedListObserver.observe(el);
                updatedFilterObserver.observe(el);
            });
        });
    }

    /**
     * Check for breakpoint
     */
    protected isMobile(): boolean {
        return window.innerWidth < 1024;
    }
}